import { Tooltip } from "antd";
import { ThunderboltOutlined, ShoppingOutlined, FireOutlined } from "@ant-design/icons";
import { fetchAiCredits } from "../../../services/customer360Service";
import {
  cardStyle, inr, num, dateTime, statusChip, chip, TONE, titleCase,
  StatTile, UsageBar, TileGrid, PanelHeader, PanelTable, PanelSkeleton,
  PanelError, usePanelData,
} from "./shared";

/**
 * The AI credit ledger.
 *
 * Two numbers matter here and they are easy to confuse: credits, which is what
 * the customer was sold, and rupees, which is what those calls actually cost us.
 * They are kept in separate columns rather than merged into one "amount".
 */
const AiCreditsTab = ({ userId, active }) => {
  const p = usePanelData(fetchAiCredits, userId, active);

  if (p.loading && !p.data) return <PanelSkeleton tiles={3} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const w = p.data?.wallet || {};

  const columns = [
    {
      title: "Date", dataIndex: "created_at", width: 165,
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
    {
      title: "Type", dataIndex: "type", width: 100,
      render: (v) => (String(v).toLowerCase() === "credit"
        ? chip("Credit", TONE.green)
        : chip(titleCase(v), TONE.red)),
    },
    {
      title: "Credits", dataIndex: "amount", width: 110, align: "right",
      /* Signed by type, because the ledger stores a magnitude — an unsigned 500
         beside a debit row reads as an addition. */
      render: (v, r) => {
        const credit = String(r.type).toLowerCase() === "credit";
        return (
          <span style={{
            fontWeight: 700, fontVariantNumeric: "tabular-nums",
            color: credit ? "#16a34a" : "#dc2626",
          }}>
            {credit ? "+" : "−"}{num(Math.abs(Number(v) || 0))}
          </span>
        );
      },
    },
    {
      title: "Balance after", dataIndex: "balance_after", width: 125, align: "right",
      render: (v) => <span style={{ fontWeight: 600, color: "#0f172a", fontVariantNumeric: "tabular-nums" }}>{num(v)}</span>,
    },
    {
      title: "Source", dataIndex: "reference_type", width: 150,
      render: (v) => (v ? chip(titleCase(v), TONE.violet) : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Description", dataIndex: "description", width: 260,
      render: (v) => (v
        ? <Tooltip title={v}><span style={{ fontSize: 12.5, color: "#64748b" }}>{v}</span></Tooltip>
        : <span style={{ color: "#cbd5e1" }}>—</span>),
    },
    {
      title: "Tokens", key: "tokens", width: 140, align: "right",
      /* In and out together: the pair is what a call cost, and splitting them
         across two columns doubles the width to say the same thing. */
      render: (_, r) => {
        const i = Number(r.tokens_input) || 0;
        const o = Number(r.tokens_output) || 0;
        if (!i && !o) return <span style={{ color: "#cbd5e1" }}>—</span>;
        return (
          <Tooltip title={`${num(i)} in · ${num(o)} out`}>
            <span style={{ fontSize: 12, color: "#475569", fontVariantNumeric: "tabular-nums" }}>
              {num(i)} <span style={{ color: "#cbd5e1" }}>/</span> {num(o)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actual cost", dataIndex: "actual_cost_inr", width: 120, align: "right",
      render: (v) => <span style={{ fontSize: 12.5, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{inr(v)}</span>,
    },
    { title: "Status", dataIndex: "status", width: 110, render: (v) => statusChip(v) },
  ];

  return (
    <>
      <TileGrid>
        <StatTile
          icon={<ThunderboltOutlined />} label="Credit balance" value={num(w.balance)}
          color="#7c3aed" bg="#f5f3ff"
        >
          <UsageBar used={w.total_credits_used} total={w.total_credits_purchased} color="#7c3aed" />
        </StatTile>
        <StatTile
          icon={<ShoppingOutlined />} label="Purchased" value={num(w.total_credits_purchased)}
          color="#2563eb" bg="#eff6ff"
        />
        <StatTile
          icon={<FireOutlined />} label="Used" value={num(w.total_credits_used)}
          color="#ea580c" bg="#fff7ed"
        />
      </TileGrid>

      <div style={cardStyle}>
        <PanelHeader
          icon={<ThunderboltOutlined />}
          title="AI credit ledger"
          subtitle="Credits bought, and what each call consumed"
          iconBg="linear-gradient(135deg,#7c3aed,#6366f1)"
        />
        <PanelTable
          columns={columns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No AI credit activity on this account yet."
          scrollX={1280}
        />
      </div>
    </>
  );
};

export default AiCreditsTab;
